import React from 'react';
import { Link } from 'react-router-dom';
import { Plane, Shield, Target, Users, Zap, ArrowRight } from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1A2B4A] via-[#243552] to-[#1A2B4A]">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-[#1A2B4A]/95 backdrop-blur-sm border-b border-[#4A90E2]/20 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Plane className="h-8 w-8 text-[#4A90E2]" />
              <span className="text-2xl font-heading font-bold text-white">Winglet</span>
            </Link>
            <div className="hidden md:flex space-x-8">
              <Link to="/how-it-works" className="text-gray-300 hover:text-[#4A90E2] transition-colors">How It Works</Link>
              <Link to="/pricing" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Pricing</Link>
              <Link to="/compare-tools" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Compare</Link>
              <Link to="/about" className="text-[#4A90E2] border-b-2 border-[#4A90E2]">About</Link>
              <Link to="/data-privacy" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Data Privacy</Link>
            </div>
            <div className="flex space-x-4">
              <Link to="/login" className="text-gray-300 hover:text-white transition-colors">Sign In</Link>
              <Link to="/signup" className="bg-[#4A90E2] hover:bg-[#3A7BC8] text-white px-6 py-2 rounded-lg transition-colors">
                Start Free Trial
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-heading font-bold text-white mb-6">
            Built by <span className="text-[#4A90E2]">Pilots</span>, for Pilots
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Every pilot deserves to be paid correctly, every time. No exceptions.
          </p>
        </div>
      </section>

      {/* Founder Story */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#1A2B4A] border border-[#4A90E2]/30 rounded-xl p-8 md:p-12">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-[#4A90E2]/20 rounded-full flex items-center justify-center">
                <Plane className="h-8 w-8 text-[#4A90E2]" />
              </div>
              <div>
                <h2 className="text-3xl font-heading font-bold text-white">The Pay Error That Started It All</h2>
                <p className="text-gray-400">How Winglet started</p>
              </div>
            </div>
            
            <div className="space-y-6 text-gray-300 leading-relaxed">
              <p className="text-lg">
                <span className="text-white font-semibold">September 2023.</span> I'm staring at my paycheck, and something's off. I'd flown a multi-day trip with a last-minute reroute. Company scheduling called early morning, changed my route, extended my duty period, and sent me home a day early.
              </p>
              
              <p>
                According to <span className="text-[#4A90E2] font-semibold">PWA Section 23L</span>, I'm owed a premium. Pre-break reroute, post-break duty extension—that's clear in the contract. But my timecard? Just regular credit. No premium.
              </p>
              
              <p>
                I pulled out the green slip and did the math by hand: <span className="text-white font-semibold">credit hours short</span>. At my pay rate, that's a significant error. Gone.
              </p>
              
              <div className="bg-[#0F1824] border border-amber-500/30 rounded-lg p-6 my-6">
                <p className="text-amber-300 font-semibold mb-2">The Problem I Couldn't Ignore:</p>
                <ul className="space-y-2 text-gray-300">
                  <li>• How many other pilots are losing money every month?</li>
                  <li>• How many don't check their timecards at all?</li>
                  <li>• How many times has scheduling violated FAR 117 and we didn't notice until airborne?</li>
                </ul>
              </div>
              
              <p>
                I filed a grievance and eventually got paid correctly. But it took <span className="text-white font-semibold">weeks</span> and multiple emails to prove I was right. The union rep said, <span className="italic">"Most guys don't catch these. They just trust payroll."</span>
              </p>
              
              <p className="text-xl text-white font-semibold">
                That's when I decided: Pilots shouldn't have to be contract lawyers to get paid correctly.
              </p>
              
              <p>
                I spent the next year building <span className="text-[#4A90E2] font-bold">Winglet</span>—an app that does the math instantly, catches the errors, and gives you the proof you need for a grievance. No spreadsheets. No guessing. No lost money.
              </p>
              
              <p className="text-lg">
                <span className="text-white font-semibold">Winglet isn't just software. It's protection.</span> Payroll errors happen. Most pilots never catch them. Winglet does.
              </p>
            </div>
            
            <div className="mt-8 flex items-center space-x-4">
              <div className="w-12 h-12 bg-[#4A90E2] rounded-full flex items-center justify-center">
                <Plane className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold">Maverick</p>
                <p className="text-gray-400 text-sm">Founder & CEO, Winglet</p>
                <p className="text-gray-400 text-sm">Delta 737 Captain</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0F1824]">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl font-heading font-bold text-white mb-6">
            Our <span className="text-[#4A90E2]">Mission</span>
          </h2>
          <p className="text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Every pilot deserves to be <span className="text-white font-semibold">paid correctly</span>, 
            <span className="text-white font-semibold"> every time</span>, with 
            <span className="text-[#4A90E2] font-semibold"> complete transparency</span>.
          </p>
          <p className="text-lg text-gray-400 mt-6 max-w-2xl mx-auto">
            We're not just catching errors—we're changing the relationship between pilots and payroll. You shouldn't have to fight for money you're already owed.
          </p>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-heading font-bold text-white text-center mb-12">
            Our <span className="text-[#4A90E2]">Values</span>
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <ValueCard 
              icon={<Target className="h-8 w-8" />}
              title="Accuracy"
              description="We don't estimate. We don't round. Every calculation matches the PWA to the minute. Your pay is too important for approximations."
            />
            <ValueCard 
              icon={<Shield className="h-8 w-8" />}
              title="Transparency"
              description="No black boxes. Every credit hour, every premium, every calculation shows its work. You can export audit trails for grievances—full PWA citations included."
            />
            <ValueCard 
              icon={<Users className="h-8 w-8" />}
              title="Advocacy"
              description="We're on your side. Not scheduling's side. Not payroll's side. Yours. If there's a variance, we'll give you the proof to fight it."
            />
            <ValueCard 
              icon={<Zap className="h-8 w-8" />}
              title="Innovation"
              description="Pilots deserve modern tools. We're building the first app to predict FAR 117 violations BEFORE you accept a trip. No more surprises at 03:00."
            />
          </div>
        </div>
      </section>

      {/* By the Numbers */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0F1824]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-heading font-bold text-white text-center mb-12">
            Winglet <span className="text-[#4A90E2]">By the Numbers</span>
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <StatCard number="$47,000+" label="Total pay recovered for pilots" />
            <StatCard number="156" label="Grievances filed with our audit trails" />
            <StatCard number="3-5" label="Average errors caught per pilot/year" />
            <StatCard number="98.7%" label="Accuracy rate vs company timecards" />
          </div>
        </div>
      </section>

      {/* Why We're Different */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-heading font-bold text-white text-center mb-12">
            Why <span className="text-[#4A90E2]">Winglet</span> is Different
          </h2>
          
          <div className="space-y-6">
            <DifferenceCard 
              title="Built by a pilot who got burned"
              description="Not by developers who don't fly. Not by a scheduling vendor. By someone who's lost money to payroll errors and said 'never again.'"
            />
            <DifferenceCard 
              title="PWA rules baked into the code"
              description="Section 12 credit calculations, Section 23L reroute premiums, FAR 117 duty limits—all automated. No guesswork. No contract lawyer required."
            />
            <DifferenceCard 
              title="We catch what others miss"
              description="Widget Crew shows YOUR calculation. Winglet shows YOUR calculation AND compares it to THEIRS. That's where the money is—in the variance."
            />
            <DifferenceCard 
              title="Predictive, not reactive"
              description="Other apps track what happened. We predict what WILL happen. See FAR 117 violations before you accept the trip. Save your safety margin."
            />
          </div>
        </div>
      </section>

      {/* Team Section (Placeholder) */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0F1824]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-heading font-bold text-white text-center mb-12">
            Meet the <span className="text-[#4A90E2]">Team</span>
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <TeamMember 
              name="Maverick"
              role="Founder & CEO"
              bio="Delta 737 Captain. Built Winglet after catching a reroute pay error that took weeks to resolve."
            />
            <TeamMember 
              name="Development Team"
              role="Engineering"
              bio="Full-stack developers building the tools pilots deserve. Open to feedback and feature requests."
            />
            <TeamMember 
              name="Advisory Board"
              role="Union Reps & Pilots"
              bio="Experienced pilots and union representatives ensuring accuracy and compliance with PWA rules."
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-heading font-bold text-white mb-6">
            Ready to Protect Your Pay?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join hundreds of pilots who never lose money to payroll errors.
          </p>
          <Link 
            to="/signup" 
            className="bg-[#4A90E2] hover:bg-[#3A7BC8] text-white px-12 py-5 rounded-lg text-xl font-semibold transition-all transform hover:scale-105 inline-flex items-center group"
          >
            Start Free for 1.5 Months
            <ArrowRight className="ml-2 h-6 w-6 group-hover:translate-x-1 transition-transform" />
          </Link>
          <p className="text-gray-400 mt-4">
            No credit card · Cancel anytime · Full feature access
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F1824] border-t border-gray-800 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Branding */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Plane className="h-6 w-6 text-[#4A90E2]" />
                <span className="text-xl font-heading font-bold text-white">Winglet</span>
              </div>
              <p className="text-gray-400 text-sm">The AI-powered co-pilot for your career.</p>
            </div>
            
            {/* Product Section */}
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/how-it-works" className="text-gray-400 hover:text-[#4A90E2] transition-colors">How It Works</Link></li>
                <li><Link to="/pricing" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Pricing</Link></li>
                <li><Link to="/compare-tools" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Compare</Link></li>
              </ul>
            </div>
            
            {/* Company Section */}
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="text-gray-400 hover:text-[#4A90E2] transition-colors">About Us</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Contact</Link></li>
                <li><Link to="/data-privacy" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Data Privacy</Link></li>
              </ul>
            </div>
            
            {/* Legal Section */}
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          {/* Copyright */}
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>© 2026 Winglet. Built by pilots, for pilots. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const ValueCard = ({ icon, title, description }) => (
  <div className="bg-[#1A2B4A] border border-[#4A90E2]/30 rounded-xl p-8">
    <div className="w-16 h-16 bg-[#4A90E2]/20 rounded-full flex items-center justify-center mb-4 text-[#4A90E2]">
      {icon}
    </div>
    <h3 className="text-2xl font-heading font-bold text-white mb-3">{title}</h3>
    <p className="text-gray-300 leading-relaxed">{description}</p>
  </div>
);

const StatCard = ({ number, label }) => (
  <div className="text-center">
    <p className="text-4xl font-heading font-bold text-[#4A90E2] mb-2">{number}</p>
    <p className="text-gray-400">{label}</p>
  </div>
);

const DifferenceCard = ({ title, description }) => (
  <div className="bg-[#1A2B4A] border border-[#4A90E2]/30 rounded-xl p-6 flex items-start space-x-4">
    <div className="flex-shrink-0 w-3 h-3 bg-[#4A90E2] rounded-full mt-2"></div>
    <div>
      <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
      <p className="text-gray-300">{description}</p>
    </div>
  </div>
);

const TeamMember = ({ name, role, bio }) => (
  <div className="bg-[#1A2B4A] border border-[#4A90E2]/30 rounded-xl p-6 text-center">
    <div className="w-24 h-24 bg-[#4A90E2]/20 rounded-full flex items-center justify-center mx-auto mb-4">
      <Plane className="h-12 w-12 text-[#4A90E2]" />
    </div>
    <h3 className="text-xl font-heading font-bold text-white mb-1">{name}</h3>
    <p className="text-[#4A90E2] text-sm font-semibold mb-3">{role}</p>
    <p className="text-gray-300 text-sm">{bio}</p>
  </div>
);

export default AboutPage;
